import { useId, useRef } from 'react';
import { plainTextToWebsiteRichText, websiteRichTextToPlainText } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../../hooks/use-website-t';
import { slotFieldLabel } from '../../lib/region-labels';
import {
  authoredRichText,
  codePointLength,
  defaultTextOf,
  hasTemplateDefault,
} from '../../lib/slot-values';
import type { SlotFieldProps } from './slot-text-field';

/**
 * A rich-text field is a plain box: a blank line ends a paragraph and a line
 * that starts with "- " is a bullet. A text that has more formatting than
 * that (bold, numbered lists of a converted store) says so before it is edited,
 * because editing it writes the plain form. A text the merchant does not touch
 * is never rewritten (the batch holds changed slots only).
 */
export function SlotRichTextField({
  slot,
  state,
  onChange,
  issueKey,
  interfaceLocale,
}: SlotFieldProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const id = useId();
  const messageId = `${id}-message`;

  const document = authoredRichText(state);
  const plain = document ? websiteRichTextToPlainText(document) : null;
  // What the field opened with decides the notice, not what has been typed since.
  const lossyAtOpen = useRef(plain?.lossy ?? false);

  const text =
    state.mode === 'authored'
      ? (plain?.text ?? '')
      : state.mode === 'default'
        ? (defaultTextOf(slot, interfaceLocale) ?? '')
        : '';
  const count = codePointLength(text);
  const max = slot.maxLength;
  const tooLong = max !== undefined && count > max;

  return (
    <div className="space-y-1.5" data-testid={`field-${slot.id}`}>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id}>{slotFieldLabel(t, slot)}</Label>
        {max === undefined ? null : (
          <span
            aria-live="polite"
            className={cn('text-xs', tooLong ? 'text-destructive' : 'text-muted-foreground')}
          >
            {t('admin.websiteTemplates.dialog.charCount', { count, max })}
          </span>
        )}
      </div>
      <Textarea
        id={id}
        rows={6}
        dir="auto"
        value={text}
        aria-invalid={issueKey !== undefined || tooLong ? true : undefined}
        aria-describedby={issueKey === undefined ? undefined : messageId}
        onChange={(event) => {
          const next = event.target.value;
          if (next === '' && !slot.required) onChange({ mode: 'empty' });
          else onChange({ mode: 'authored', value: { v: plainTextToWebsiteRichText(next) } });
        }}
      />
      <p className="text-muted-foreground text-xs">{t('admin.websiteTemplates.richText.hint')}</p>
      {lossyAtOpen.current ? (
        <p data-testid={`lossy-${slot.id}`} className="text-muted-foreground text-xs">
          {t('admin.websiteTemplates.richText.lossy')}
        </p>
      ) : null}
      {issueKey === undefined ? null : (
        <p id={messageId} role="alert" className="text-destructive text-xs">
          {t(issueKey)}
        </p>
      )}
      {hasTemplateDefault(slot) || !slot.required ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {hasTemplateDefault(slot) && state.mode !== 'default' ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => onChange({ mode: 'default' })}
            >
              {t('admin.websiteTemplates.dialog.useDefault')}
            </Button>
          ) : null}
          {!slot.required && state.mode !== 'empty' ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => onChange({ mode: 'empty' })}
            >
              {t('admin.websiteTemplates.dialog.leaveEmpty')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
